import { Link } from "react-router-dom";
import { Shield, Check, Phone, Star, MapPin, AlertTriangle, Clock } from "lucide-react";
import PestOnlyCalculator from "../components/PestOnlyCalculator";
import { Logo } from "../components/Layout";
import { Header } from "../components/Layout";
import IpswichBreadcrumb from "../components/IpswichBreadcrumb";

const suburbs = ["Ipswich CBD", "Brassall", "Booval", "Bundamba", "Goodna", "Redbank Plains", "Collingwood Park", "Bellbird Park", "Ripley", "Springfield", "North Ipswich", "East Ipswich", "Leichhardt", "One Mile", "Woodend"];

export default function CockroachTreatmentIpswichPage() {
  return (
    <>
      <>
        <title>Cockroach Treatment Ipswich | Fast & Effective | Pest Free</title>
        <meta name="description" content="Professional cockroach treatment in Ipswich QLD. German, American & Australian cockroach removal. Same-day service, family-safe chemicals. QBCC licensed. Get an instant quote." />
        <link rel="canonical" href="https://pestfreebrisbane.com.au/cockroach-treatment-ipswich" />
      </>

      <Header />
      <div className="min-h-screen bg-slate-50">
        <IpswichBreadcrumb current="Cockroach Treatment" />
        <section className="bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#E74C3C]/20 text-white pt-28 pb-16 md:pt-32 md:pb-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#E74C3C]/20 border border-[#E74C3C]/30 rounded-full px-4 py-1.5 mb-5">
                  <MapPin className="w-4 h-4 text-[#E74C3C]" />
                  <span className="text-sm font-medium text-[#E74C3C]">Ipswich QLD</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight" data-testid="cockroach-h1">
                  Cockroach Treatment <span className="text-[#7ABC32]">Ipswich</span>
                </h1>
                <p className="text-lg text-slate-300 mb-6">
                  Ipswich's subtropical climate makes it a breeding ground for cockroaches. Our professional treatments target German, American and Australian cockroaches at every life stage — eggs, nymphs and adults — so they don't come back.
                </p>
                <div className="flex flex-wrap gap-3 mb-6">
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />Same Day Service</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />QBCC Licensed</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />Child & Pet Safe</div>
                </div>
                <div className="flex items-center gap-3">
                  <a href="tel:0737081422" className="flex items-center gap-2 bg-[#7ABC32] hover:bg-[#6AAD28] text-white font-semibold px-6 py-3 rounded-full transition-all" data-testid="cockroach-call-btn">
                    <Phone className="w-5 h-5" /> (07) 3708 1422
                  </a>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                    <span className="text-sm text-slate-300 ml-1">4.9 on Google</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-center">
                <Logo className="h-32 md:h-40 opacity-90" />
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16" id="calculator">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800">Get Your Cockroach Treatment Quote</h2>
              <p className="text-slate-600 mt-2">Instant pricing for Ipswich homes and businesses</p>
            </div>
            <PestOnlyCalculator source="cockroach_ipswich" />
          </div>
        </section>

        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">Why Cockroaches Thrive in Ipswich</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: "Warm, Humid Climate", desc: "Ipswich's subtropical weather, with hot summers and mild winters, creates ideal year-round breeding conditions for cockroaches. They're active in every season.", icon: AlertTriangle, color: "#E74C3C" },
                { title: "Older Homes & Timber Frames", desc: "Many Ipswich homes — especially in suburbs like Booval, Brassall and North Ipswich — have older timber construction with gaps and crevices that cockroaches exploit.", icon: Shield, color: "#D2691E" },
                { title: "New Estates & Construction", desc: "Rapid development in Ripley, Springfield and Bellbird Park disturbs existing cockroach habitats, pushing them into nearby homes and newly built properties.", icon: AlertTriangle, color: "#E74C3C" },
                { title: "Proximity to Waterways", desc: "The Bremer River and Bundamba Creek corridors provide moisture and organic debris that attract American cockroaches in large numbers to surrounding suburbs.", icon: Shield, color: "#5BC0DE" },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-5 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: item.color + '15' }}>
                    <item.icon className="w-5 h-5" style={{ color: item.color }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">{item.title}</h3>
                    <p className="text-sm text-slate-600 mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">Our Cockroach Treatment Process</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { step: "1", title: "Inspection", desc: "We identify cockroach species, entry points and harbourage areas throughout your Ipswich property. Different species require different treatment approaches." },
                { step: "2", title: "Targeted Treatment", desc: "Professional-grade gel baits, residual sprays and dusting in roof voids, wall cavities, kitchen and bathroom areas. Safe for families and pets once dry." },
                { step: "3", title: "Prevention Plan", desc: "We seal entry points, advise on sanitation and provide follow-up warranty. Our treatments include a 6 to 12 month warranty depending on the plan." },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-6 border border-slate-200 text-center">
                  <div className="w-10 h-10 rounded-full bg-[#E74C3C] text-white font-bold text-lg flex items-center justify-center mx-auto mb-3">{item.step}</div>
                  <h3 className="font-bold text-slate-800 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">Cockroach Treatment FAQ — Ipswich</h2>
            <div className="space-y-4">
              {[
                { q: "How quickly can you treat cockroaches in Ipswich?", a: "We offer same-day and next-day cockroach treatment across all Ipswich suburbs. Most jobs are completed within 1-2 hours depending on property size." },
                { q: "Is the treatment safe for children and pets?", a: "Yes. We use professional-grade products that are safe for families and pets once dry (typically 30-60 minutes). We'll advise you on any precautions specific to your property." },
                { q: "What types of cockroaches are common in Ipswich?", a: "German cockroaches are the most common indoor species in Ipswich, especially in kitchens and bathrooms. American and Australian cockroaches are prevalent outdoors and in older homes near waterways." },
                { q: "How long does the treatment last?", a: "Our standard treatment includes a 6-month warranty, and our comprehensive treatment comes with a 12-month warranty. Regular annual treatments are recommended in Ipswich due to the climate." },
                { q: "Do you treat commercial properties for cockroaches?", a: "Yes. We provide cockroach treatment for restaurants, cafes, offices, warehouses and rental properties across Ipswich. We can also provide compliance certificates." },
              ].map((item, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                  <h3 className="font-bold text-slate-800 mb-2">{item.q}</h3>
                  <p className="text-sm text-slate-600">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Cockroach Treatment Across Ipswich</h2>
            <div className="flex flex-wrap justify-center gap-2">
              {suburbs.map(s => (
                <span key={s} className="bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700">{s}</span>
              ))}
            </div>
            <p className="text-slate-500 text-sm mt-4">And all surrounding areas in the Ipswich region</p>
          </div>
        </section>

        <section className="py-8 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-lg font-bold text-slate-800 text-center mb-4">Related Pest Services in Ipswich</h2>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/pest-control-ipswich" className="text-sm text-[#E74C3C] hover:underline font-medium">Pest Control Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/termite-inspection-ipswich" className="text-sm text-[#D2691E] hover:underline font-medium">Termite Inspection Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/termite-treatment-ipswich" className="text-sm text-[#D2691E] hover:underline font-medium">Termite Treatment Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/bird-control-ipswich" className="text-sm text-[#5BC0DE] hover:underline font-medium">Bird Control Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/end-of-lease-pest-control-ipswich" className="text-sm text-[#7ABC32] hover:underline font-medium">End of Lease Pest Control</Link>
            </div>
          </div>
        </section>

        <section className="py-12 bg-gradient-to-r from-[#E74C3C] to-[#c0392b] text-white text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Get Rid of Cockroaches Today</h2>
            <p className="text-lg text-white/80 mb-6">Fast, effective cockroach treatment across Ipswich. Same-day service available.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:0737081422" className="inline-flex items-center gap-2 bg-white text-[#E74C3C] font-bold px-8 py-3 rounded-full hover:bg-slate-100 transition-all">
                <Phone className="w-5 h-5" /> Call (07) 3708 1422
              </a>
              <a href="#calculator" className="inline-flex items-center gap-2 border-2 border-white text-white font-bold px-8 py-3 rounded-full hover:bg-white/10 transition-all">
                <Clock className="w-5 h-5" /> Get Instant Quote
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
